import type { ComponentProps, FC } from 'react';
import { DatePicker } from 'antd';
import type { DatePickerProps } from 'antd';

import { platformDatePickerDisplayFormat, platformDateTimePickerDisplayFormat } from '@/theme';
import { DEFAULT_DATE_FORMAT } from '@/utilities';

type PlatformDateValue = NonNullable<DatePickerProps['value']>;

export type PlatformDatePickerProps<ValueType = PlatformDateValue> = DatePickerProps<ValueType>;
export type PlatformRangePickerProps = ComponentProps<typeof DatePicker.RangePicker>;

const dateFormats: NonNullable<DatePickerProps['format']> = [
    platformDatePickerDisplayFormat,
    'MMM. D, YYYY',
    'MMM D, YYYY',
    DEFAULT_DATE_FORMAT,
    'M/D/YYYY',
];

const dateTimeFormats: NonNullable<DatePickerProps['format']> = [
    platformDateTimePickerDisplayFormat,
    'MMM. D, YYYY HH:mm:ss',
    'MMM D, YYYY HH:mm:ss',
    `${DEFAULT_DATE_FORMAT} HH:mm:ss`,
    'M/D/YYYY HH:mm:ss',
];

const resolveFormat = (
    format: DatePickerProps['format'] | PlatformRangePickerProps['format'],
    picker: DatePickerProps['picker'] | PlatformRangePickerProps['picker'],
    showTime: DatePickerProps['showTime'] | PlatformRangePickerProps['showTime'],
) => {
    if (format || (picker && picker !== 'date')) {
        return format;
    }

    return showTime ? dateTimeFormats : dateFormats;
};

/**
 * The platform date picker owns the user-facing date format while preserving
 * Dayjs values so API serializers can continue using their existing contracts.
 */
const PlatformDatePicker = <ValueType = PlatformDateValue,>({
    format,
    picker,
    showTime,
    ...props
}: PlatformDatePickerProps<ValueType>): JSX.Element => (
    <DatePicker<ValueType>
        {...props}
        format={resolveFormat(format, picker, showTime)}
        picker={picker}
        showTime={showTime}
    />
);

const PlatformRangePicker: FC<PlatformRangePickerProps> = ({ format, picker, showTime, ...props }): JSX.Element => (
    <DatePicker.RangePicker
        {...props}
        format={resolveFormat(format, picker, showTime)}
        picker={picker}
        showTime={showTime}
    />
);

export { PlatformRangePicker };
export default PlatformDatePicker;
